import { useState, useEffect } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient, setUnauthorizedHandler, apiRequest } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import Dashboard from "@/pages/dashboard";
import Profile from "@/pages/profile";
import LoginPage from "@/pages/login";
import ForgotPasswordPage from "@/pages/forgot-password";
import MagicLinkPage from "@/pages/magic-link";
import AdminPage from "@/pages/admin";
import MonthlyRecapPage from "@/pages/monthly-recap";
import SeasonRecapPage from "@/pages/season-recap";
import Unsubscribe from "@/pages/unsubscribe";
import { useTokenRefresh } from "@/hooks/useTokenRefresh";
import { LanguageSync } from "@/components/language-sync";
import { prepareNotificationRuntime, onForegroundNativeNotification, onBackgroundNativeNotification } from "@/lib/notifications";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { CookieConsent } from "@/components/cookie-consent";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { ForceUpdateManager } from "./components/ForceUpdateManager";
import { SmartAppBanner } from "./components/SmartAppBanner";

// Dedicated component to handle Capacitor deep links using wouter's useLocation hook
function DeepLinkHandler() {
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const navigateToSlug = (rawUrl: string) => {
      try {
        const parsed = new URL(rawUrl);
        const slug = parsed.pathname + parsed.search;
        if (slug) {
          console.log('[DeepLinkHandler] Navigating to:', slug);
          setLocation(slug);
        }
      } catch (e) {
        console.error('[DeepLinkHandler] Failed to parse deep link URL:', e);
      }
    };

    // Case 1: app launched cold from a deep link
    CapacitorApp.getLaunchUrl().then((result) => {
      if (result?.url) {
        console.log('[DeepLinkHandler] Cold launch URL:', result.url);
        navigateToSlug(result.url);
      }
    });

    // Case 2: app was already running, brought to foreground via a link
    let removeListener: (() => void) | undefined;
    CapacitorApp.addListener('appUrlOpen', (data) => {
      console.log('[DeepLinkHandler] appUrlOpen:', data.url);
      navigateToSlug(data.url);
    }).then((handle) => {
      removeListener = () => handle.remove();
    });

    // Case 3 (Fallback for Android Intents): Custom MainActivity JS Bridge
    // MainActivity injects this event because Capacitor's appUrlOpen drops the intent://
    const handleNativeDeepLink = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        console.log('[DeepLinkHandler] Caught nativeDeepLink Event:', customEvent.detail);
        setLocation(customEvent.detail);
      }
    };
    window.addEventListener('nativeDeepLink', handleNativeDeepLink);

    // Check if the deep link was injected before this component mounted (Cold Boot)
    const pendingNativeDeepLink = (window as any)._pendingNativeDeepLink;
    if (pendingNativeDeepLink) {
      console.log('[DeepLinkHandler] Found pending nativeDeepLink on mount:', pendingNativeDeepLink);
      setLocation(pendingNativeDeepLink);
      delete (window as any)._pendingNativeDeepLink;
    }

    return () => {
      removeListener?.();
      window.removeEventListener('nativeDeepLink', handleNativeDeepLink);
    };
  }, [setLocation]);

  return null;
}

function Router({ token, playerId, onLogout, onProfileClick, showProfile, onBackToDashboard, onShowTour, initialProfileSection }: {
  token: string | null;
  playerId: string | null;
  onLogout: () => void;
  onProfileClick: () => void;
  showProfile: boolean;
  onBackToDashboard: () => void;
  onShowTour: () => void;
  initialProfileSection?: string;
}) {
  if (showProfile) {
    return <Profile onLogout={onLogout} onBack={onBackToDashboard} playerId={playerId} onShowTour={onShowTour} initialSection={initialProfileSection} />;
  }

  return (
    <Switch>
      <Route path="/" component={() => <Dashboard onLogout={onLogout} onProfileClick={onProfileClick} playerId={playerId} />} />
      <Route path="/recap/:playerId/:month" component={MonthlyRecapPage} />
      <Route path="/recap/season/:clubId/:seasonId/:playerId" component={SeasonRecapPage} />
      <Route path="/auth/magic">{() => { window.location.href = "/"; return null; }}</Route>
      <Route path="/signup">{() => { window.location.href = "/"; return null; }}</Route>
      <Route path="/admin" component={() => <AdminPage />} />
      <Route path="/clubs" component={() => {
        // eslint-disable-next-react-hooks/rules-of-hooks
        const [, setLocation] = useLocation();
        const search = window.location.search;
        const params = new URLSearchParams(search);
        const searchQuery = params.get("search") || "";

        // Redirect to dashboard with clubs tab and search query pre-filled
        setLocation(`/?tab=clubs${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`, { replace: true });

        return null;
      }} />
      <Route path="/profile" component={() => {
        const search = window.location.search;
        const params = new URLSearchParams(search);
        const section = params.get("section") || undefined;
        // eslint-disable-next-react-hooks/rules-of-hooks
        const [, setLocation] = useLocation();

        return (
          <Profile
            onLogout={onLogout}
            onBack={() => setLocation('/')} // Use setLocation to return to root when accessed via direct URL
            playerId={playerId}
            onShowTour={onShowTour}
            initialSection={section || initialProfileSection}
          />
        );
      }} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  const [token, setToken] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const [profileSection, setProfileSection] = useState<string | undefined>(undefined);

  // Apply saved theme on startup
  useEffect(() => {
    const saved = localStorage.getItem("theme");
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    if (saved === "dark" || (!saved && prefersDark)) {
      document.documentElement.classList.add("dark");
    }
  }, []);

  useEffect(() => {
    // Check for existing token and playerId in localStorage
    try {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        const savedToken = localStorage.getItem("authToken");
        const savedPlayerId = localStorage.getItem("playerId");
        if (savedToken) {
          setToken(savedToken);
          setPlayerId(savedPlayerId);
        }
      }
    } catch (error) {
      console.error("Error accessing localStorage:", error);
    }
    setIsLoading(false);
  }, []);

  // Set up global 401 handler for automatic logout on token expiration
  // Même chemin que la déconnexion manuelle : inclut le correctif Capacitor (sinon, sur mobile,
  // l'app restait affichée connectée avec un jeton refusé)
  useEffect(() => {
    setUnauthorizedHandler(() => {
      console.log("🔒 Session expired - logging out automatically");
      handleLogout();
    });
  }, []);

  // Enable automatic token refresh
  useTokenRefresh();

  // Initialize notification listeners when user is logged in
  useEffect(() => {
    if (!token) {
      // Clean up any existing listeners when token is cleared
      return;
    }

    let foregroundCleanup: (() => void) | undefined;
    let backgroundCleanup: (() => void) | undefined;
    let isMounted = true;

    const initializeNotifications = async () => {
      try {
        // Double-check token still exists (might have been cleared during async operation)
        if (!isMounted || !token) return;

        // Add a small delay to ensure UI is responsive before initializing notifications
        // This prevents blocking the interface after login, especially on web
        await new Promise(resolve => setTimeout(resolve, 200));

        if (!isMounted || !token) return;

        await prepareNotificationRuntime();
        if (!isMounted) return;

        console.log('[App] Notification runtime prepared');

        foregroundCleanup = await onForegroundNativeNotification((payload) => {
          if (!isMounted) return;
          console.log('[App] Foreground notification received:', payload);
        });

        backgroundCleanup = await onBackgroundNativeNotification((payload) => {
          if (!isMounted) return;
          console.log('[App] Background notification received:', payload);
          const data = payload.data as Record<string, any> | undefined;
          if (data?.matchId && !data.cancelledAt) {
            const filter = data.action === 'validate' ? 'clubs' : 'mine';
            window.dispatchEvent(new CustomEvent('nativeDeepLink', { detail: `/?tab=matches&filter=${filter}&matchId=${data.matchId}` }));
          } else if (data?.matchId && data.cancelledAt) {
            window.dispatchEvent(new CustomEvent('nativeDeepLink', { detail: '/' }));
          } else if (data?.type === 'club_join_request' && data.clubId) {
            window.dispatchEvent(new CustomEvent('nativeDeepLink', { detail: `/clubs/${data.clubId}` }));
          } else if (data?.type === 'club_join_accepted' && data.clubId) {
            window.dispatchEvent(new CustomEvent('nativeDeepLink', { detail: `/clubs/${data.clubId}` }));
          } else if (data?.type === 'fft_ranking_updated') {
            window.dispatchEvent(new CustomEvent('nativeDeepLink', { detail: '/?tab=players' }));
          }
        });

        if (isMounted) {
          console.log('[App] Notification listeners initialized');
        }
      } catch (error) {
        console.error('[App] Failed to initialize notification listeners:', error);
        // Don't block the UI if notifications fail to initialize
      }
    };

    // Initialize notifications asynchronously without blocking
    initializeNotifications().catch(error => {
      console.error('[App] Error in notification initialization:', error);
    });

    return () => {
      isMounted = false;
      if (foregroundCleanup) {
        try {
          foregroundCleanup();
        } catch (error) {
          console.error('[App] Error cleaning up foreground listener:', error);
        }
      }
      if (backgroundCleanup) {
        try {
          backgroundCleanup();
        } catch (error) {
          console.error('[App] Error cleaning up background listener:', error);
        }
      }
    };
  }, [token]);

  // Handle login logic
  const handleLogin = (newToken: string, newPlayerId?: string) => {
    // Clear old authentication data first
    setToken(null);
    setPlayerId(null);
    setShowProfile(false);

    // Clear localStorage
    try {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        localStorage.removeItem("authToken");
        localStorage.removeItem("playerId");
        localStorage.removeItem("isAdmin");
      }
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }

    // Clear all query cache to ensure fresh data
    queryClient.clear();

    // Set new authentication data
    setToken(newToken);
    try {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        localStorage.setItem("authToken", newToken);

        if (newPlayerId) {
          setPlayerId(newPlayerId);
          localStorage.setItem("playerId", newPlayerId);
        }
      }
    } catch (error) {
      console.error("Error setting localStorage:", error);
    }

    // Force a small delay to ensure state is fully updated and React has time to re-render
    // This prevents race conditions where Dashboard tries to load data before token is ready
    // Also prevents the UI from freezing due to immediate query invalidations
    setTimeout(() => {
      // Invalidate queries to refresh player list and rankings after login
      queryClient.invalidateQueries({ queryKey: ["/api/players"] });
      queryClient.invalidateQueries({ queryKey: ["/api/players/rankings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });

      // Ping to log the session
      const cap = typeof window !== 'undefined' ? (window as any).Capacitor : null;
      const platform = cap?.isNativePlatform?.() ? (cap.getPlatform?.() === 'ios' ? 'ios' : 'android') : 'web';
      apiRequest('POST', '/api/auth/ping', { platform }).catch(() => {});
    }, 100);
  };

  const handleLogout = () => {
    // Clear all state first
    setToken(null);
    setPlayerId(null);
    setShowProfile(false);

    // Clear localStorage
    try {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        localStorage.removeItem("authToken");
        localStorage.removeItem("playerId");
        localStorage.removeItem("isAdmin");
      }
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }

    // Clear all query cache to prevent stale requests
    queryClient.clear();

    // Force a small delay on mobile to ensure state is fully cleared
    // This helps prevent race conditions with Capacitor's localStorage
    if (typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.()) {
      setTimeout(() => {
        // Force re-render by updating state again
        setToken(null);
      }, 100);
    }
  };

  const handleProfileClick = (section?: string) => {
    setShowProfile(true);
    setProfileSection(section);
  };

  const handleBackToDashboard = () => {
    setShowProfile(false);
    setProfileSection(undefined);
  };

  const handleShowTour = () => {
    setShowProfile(false);
    // The tour will be triggered by the Dashboard component
    // when it detects the localStorage flag has been cleared
  };

  if (isLoading) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-lg text-gray-600">Loading...</div>
          </div>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ServerStatusListener />
        {token && <LanguageSync />}
        <CookieConsent />
        <SmartAppBanner />
        <DeepLinkHandler />
        <Toaster />
        <ForceUpdateManager>
          {token && playerId ? (
            <Router
              token={token}
              playerId={playerId}
              onLogout={handleLogout}
              onProfileClick={handleProfileClick}
              showProfile={showProfile}
              onBackToDashboard={handleBackToDashboard}
              onShowTour={handleShowTour}
              initialProfileSection={profileSection}
            />
          ) : (
            <Switch>
              <Route path="/recap/:playerId/:month" component={MonthlyRecapPage} />
              <Route path="/recap/season/:clubId/:seasonId/:playerId" component={SeasonRecapPage} />
              <Route path="/forgot-password" component={ForgotPasswordPage} />
              <Route path="/signup" component={() => <LoginPage onLogin={handleLogin} />} />
              <Route path="/auth/magic" component={() => <MagicLinkPage onLogin={handleLogin} />} />
              <Route path="/unsubscribe" component={Unsubscribe} />
              <Route path="/admin" component={() => <AdminPage />} />
              <Route path="/profile" component={() => <LoginPage onLogin={handleLogin} />} />
              <Route path="/" component={() => <LoginPage onLogin={handleLogin} />} />
              <Route component={() => <LoginPage onLogin={handleLogin} />} />
            </Switch>
          )}
        </ForceUpdateManager>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function ServerStatusListener() {
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    const handleServerUnavailable = () => {
      toast({
        title: t('common.maintenance'),
        description: t('common.maintenanceDesc'),
        variant: "destructive",
        duration: Infinity, // Persistent until dismissed or page reload
      });
    };

    window.addEventListener('server-unavailable', handleServerUnavailable);
    return () => window.removeEventListener('server-unavailable', handleServerUnavailable);
  }, [toast, t]);

  return null;
}

export default App;
